/* COWBELL curated-first v1.2 (2026-10-13; v1.1 2026-08-16; v1 2026-08-14)
 * v1.2: a fetched card takes its neighbours' card shape (--ratio-percent), so the pinned card
 * matches on collections with their own card ratio (back prints = square; the ?view=card
 * fragment renders the store default, portrait).
 * v1.1: fetches start at script run (not DOM ready), slot 1 is guarded for 20s
 * against post-load grid re-renders, window.__cowbellPin exposes status.
 * Pins the ?first= product into slot 1 of the collection grid on page 1.
 * Built for Meta catalog deep links:
 *   /collections/<any>?first=<product URL (encoded or raw) or handle>[&variant=<id>]
 * Inert without the ?first= param.
 * Revert: delete this asset, templates/product.card.liquid, and the marked
 * COWBELL block in sections/main-collection-product-grid.liquid.
 */
(() => {
  const qp = new URLSearchParams(location.search);
  let raw = (qp.get('first') || '').trim();
  if (!raw) return;
  if (parseInt(qp.get('page') || '1', 10) > 1) return;

  try {
    if (/%[0-9A-Fa-f]{2}/.test(raw)) raw = decodeURIComponent(raw);
  } catch (e) {
    /* keep raw as-is */
  }

  let handle = '';
  let variant = (qp.get('variant') || '').trim();
  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw);
      const segs = u.pathname.split('/').filter(Boolean);
      const i = segs.indexOf('products');
      if (i > -1 && segs[i + 1]) handle = segs[i + 1];
      if (!variant) variant = u.searchParams.get('variant') || '';
    } catch (e) {
      /* unparseable URL param: stay inert */
    }
  } else {
    handle = raw.split('/').filter(Boolean).pop() || '';
  }
  handle = handle.toLowerCase();
  if (!/^[a-z0-9][a-z0-9._-]*$/.test(handle)) return;
  if (!/^\d+$/.test(variant)) variant = '';

  const status = { v: '1.2', handle, variant, stage: 'parsed', repins: 0 };
  window.__cowbellPin = status;

  // Start both network requests immediately; the grid lookup happens when the DOM is ready.
  const productJson = fetch(`/products/${handle}.js`)
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
  const cardHtml = fetch(`/products/${handle}?view=card`)
    .then((r) => (r.ok ? r.text() : ''))
    .catch(() => '');

  const onReady = (fn) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  };

  onReady(() => {
    const grid = document.getElementById('product-grid');
    if (!grid || !grid.classList.contains('product-grid')) {
      status.stage = 'no-grid';
      return;
    }
    status.stage = 'grid-found';

    const findExistingCard = () => {
      const anchors = grid.querySelectorAll('a[href*="/products/"]');
      for (const a of anchors) {
        const m = (a.pathname || '').match(/\/products\/([^/?#]+)/);
        if (m && decodeURIComponent(m[1]).toLowerCase() === handle) {
          const li = a.closest('li.grid__item');
          if (li) return li;
        }
      }
      return null;
    };

    const setVariantOnLinks = (li) => {
      if (!variant) return;
      li.querySelectorAll('a[href*="/products/"]').forEach((a) => {
        try {
          const u = new URL(a.getAttribute('href'), location.origin);
          u.searchParams.set('variant', variant);
          a.setAttribute('href', u.pathname + u.search);
        } catch (e) {
          /* leave link untouched */
        }
      });
    };

    const swapVariantImage = (li, product) => {
      if (!variant) return;
      const v = (product.variants || []).find((x) => String(x.id) === variant);
      const src = v && v.featured_image && v.featured_image.src;
      if (!src) return;
      const img = li.querySelector('.card__media img');
      if (!img) return;
      const sep = src.includes('?') ? '&' : '?';
      img.srcset = [165, 360, 533, 720, 940, 1066]
        .map((w) => `${src}${sep}width=${w} ${w}w`)
        .join(', ');
      img.src = `${src}${sep}width=533`;
      if (v.title) img.alt = `${product.title} - ${v.title}`;
    };

    // A fetched card renders the store-default shape; copy the grid's own card shape onto it.
    const matchNeighborShape = (li) => {
      const ref = Array.from(grid.children).find((x) => x !== li && x.matches && x.matches('li.grid__item'));
      if (!ref) return;
      ['.card', '.card__inner'].forEach((sel) => {
        const src = ref.querySelector(sel);
        const dst = li.querySelector(sel);
        if (!src || !dst) return;
        const r = getComputedStyle(src).getPropertyValue('--ratio-percent').trim();
        if (/^\d+(\.\d+)?%$/.test(r)) dst.style.setProperty('--ratio-percent', r);
      });
      status.shape = getComputedStyle(ref.querySelector('.card__inner') || ref).getPropertyValue('--ratio-percent').trim();
    };

    const fireViewContent = (product) => {
      const v = variant && (product.variants || []).find((x) => String(x.id) === variant);
      const vid = variant || (product.variants && product.variants[0] && String(product.variants[0].id)) || '';
      if (!vid) return;
      const price = ((v && v.price) || product.price || 0) / 100;
      let attempts = 0;
      const send = () => {
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'ViewContent', {
            content_ids: [`shopify_US_${product.id}_${vid}`],
            content_type: 'product',
            content_name: product.title,
            currency: 'USD',
            value: price,
          });
        } else if (attempts++ < 5) {
          setTimeout(send, 800);
        }
      };
      send();
    };

    const existing = findExistingCard();
    status.mode = existing ? 'move' : 'fetch';
    const liPromise = existing
      ? Promise.resolve(existing)
      : cardHtml.then((html) => {
          if (!html || html.indexOf('grid__item') === -1) return null;
          const tpl = document.createElement('template');
          tpl.innerHTML = html.trim();
          return tpl.content.querySelector('li.grid__item');
        });

    liPromise.then((li) => {
      if (!li) {
        status.stage = existing ? 'move-failed' : 'card-fetch-failed';
        return;
      }
      setVariantOnLinks(li);
      if (!existing) matchNeighborShape(li);
      li.setAttribute('data-cowbell-pinned', handle);
      const pin = () => {
        const g = document.getElementById('product-grid');
        if (!g) return;
        if (g.firstElementChild !== li) g.insertBefore(li, g.firstElementChild);
      };
      pin();
      status.stage = 'inserted';
      status.insertedAt = Math.round(performance.now());

      // Defend slot 1 for a short window: theme scripts or apps that re-render the grid after
      // load (facets, hide-sold-out apps, etc.) would otherwise silently drop the pinned card.
      // Only while the URL still carries first= (a shopper's own filter/sort change drops it).
      const container = document.getElementById('ProductGridContainer') || document.body;
      const guard = new MutationObserver(() => {
        if (!/[?&]first=/.test(location.search)) return;
        const g = document.getElementById('product-grid');
        if (!g) return;
        if (g.firstElementChild !== li || !li.isConnected) {
          pin();
          status.repins = (status.repins || 0) + 1;
        }
      });
      guard.observe(container, { childList: true, subtree: true });
      setTimeout(() => guard.disconnect(), 20000);

      productJson.then((p) => {
        if (!p) return;
        swapVariantImage(li, p);
        fireViewContent(p);
        status.stage = 'complete';
      });
    });
  });
})();
